import { inject, Injectable, PLATFORM_ID, REQUEST } from '@angular/core';

import { Store } from '@ngrx/store';
import { tap, take, switchMap, catchError, map, filter } from 'rxjs/operators';

import * as fromStore from '../store';
import { Observable, of } from 'rxjs';
import { SetTitle, SetTags, SetTheme, SetNavTransparency, SetCanonicalUrl, SetAlternateUrls } from '../../store';
import { StringUtility } from '@teamfoster/sdk/text-utility';
import { FromDictionaryPipe } from '@teamfoster/sdk/dictionary-ngrx';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { BASE_URL } from '@teamfoster/sdk/ssr';

@Injectable({ providedIn: 'root' })
export class LandingPageMetaGuard {
  private platformId = inject(PLATFORM_ID);
  private store = inject(Store<fromStore.LandingState>);
  private router = inject(Router);
  private baseUrl = inject(BASE_URL, { optional: true }) || '';

  private dict = new FromDictionaryPipe();
  // remove trailing slash from hostname if present
  private hostname = isPlatformBrowser(this.platformId) ? `https://${document.location.hostname}` : this.baseUrl.replace(/\/$/, '');

  canActivate(): Observable<boolean> {
    return this.checkStore().pipe(
      switchMap(() => of(true)),
      catchError(() => of(false))
    );
  }

  checkStore(): Observable<boolean> {
    return this.store.select(fromStore.getSelectedLandingPage).pipe(
      tap(page => {
        if (!page) {
          return;
        }

        this.store.dispatch(SetTheme({ theme: page?.colorTheme || 'palette-gray' }));
        this.store.dispatch(SetNavTransparency({ transparent: !!page?.header.image?.absoluteUrl && page.header.transparentImage }));
        this.store.dispatch(
          SetCanonicalUrl({ url: `${this.hostname}${this.router.url.split('?')[0]}${page.slug === 'home' ? '' : page.slug}` })
        );
        this.store.dispatch(SetAlternateUrls({ path: `${this.router.url.split('?')[0]}` }));
        this.store.dispatch(
          SetTitle({
            title: `${page?.metaData.title} ${
              this.dict.transform('meta-titel-suffix') ? '- ' + this.dict.transform('meta-titel-suffix') : ''
            }`,
          })
        );
        this.store.dispatch(
          SetTags({
            properties: {
              'description': StringUtility.Truncate(StringUtility.stripHtmlTags(page.metaData.description || ''), 157, '...'),
              'og:image': page.metaData.imageUrl ? `https:${page.metaData.imageUrl}` : '',
              'og:title': `${page.metaData.title} ${this.dict.transform('meta-titel-suffix')}`,
              'og:description': StringUtility.Truncate(StringUtility.stripHtmlTags(page.metaData.description || ''), 157, '...'),
            },
          })
        );
      }),
      filter(a => a?.id > 0),
      map(a => true),
      take(1)
    );
  }
}
